/**
 * Cost projection calculator.
 *
 * For each rule in a plugin's list, compute:
 *   - How many studies in the last N days would have matched this rule
 *   - Projected monthly cost at the admin's declared per-call rate
 *
 * Historical data comes from the EdgePACS worklist via apiCall('wl.get')
 * - we re-apply the rule's match conditions to each row rather than
 * running a separate SQL query. Keeps the cost calculator independent of
 * schema changes.
 */

import { apiCall } from '../../../api/client';
import { matchesStudy } from './evaluator';
import type { Rule, StudyFacts } from './types';

export interface RuleProjection {
  rule: Rule;
  matchCount7d: number;
  matchCount30d: number;
  estimatedMonthlyCalls: number;
  estimatedMonthlyCost: number;
}

export interface PluginProjection {
  perCallCost: number;
  monthlyBudget?: number;
  totalMatchCount7d: number;
  totalEstimatedMonthlyCalls: number;
  totalEstimatedMonthlyCost: number;
  overBudget: boolean;
  rules: RuleProjection[];
}

interface WorklistRow {
  studyId: number | string;
  modality?: string;
  studyDescription?: string;
  bodyPart?: string;
  institution?: string;
  referringPhysician?: string;
  patientAge?: string;
  // atlrestapi doesn't currently expose sendingAE on the worklist row;
  // when that lands we can wire it in.
}

/** Query the last 30 days of worklist, project per-rule matches + cost. */
export async function projectPluginCost(
  rules: Rule[],
  perCallCost: number,
  monthlyBudget?: number,
): Promise<PluginProjection> {
  const studies = await fetchRecentStudies();

  const rulesProj: RuleProjection[] = rules.map((rule) => {
    const matched7 = studies.filter(
      (s) => isWithinDays(s.studyDate, 7) && matchesStudy(rule.match, rowToFacts(s)),
    ).length;
    const matched30 = studies.filter(
      (s) => isWithinDays(s.studyDate, 30) && matchesStudy(rule.match, rowToFacts(s)),
    ).length;
    const monthly = Math.round((matched30 / 30) * 30);  // smooth over 30d
    return {
      rule,
      matchCount7d: matched7,
      matchCount30d: matched30,
      estimatedMonthlyCalls: monthly,
      estimatedMonthlyCost: rule.action === 'send' ? monthly * perCallCost : 0,
    };
  });

  const total7 = rulesProj.filter((r) => r.rule.action === 'send').reduce((n, r) => n + r.matchCount7d, 0);
  const totalMonthly = rulesProj.filter((r) => r.rule.action === 'send').reduce((n, r) => n + r.estimatedMonthlyCalls, 0);
  const totalCost = rulesProj.filter((r) => r.rule.action === 'send').reduce((n, r) => n + r.estimatedMonthlyCost, 0);

  return {
    perCallCost,
    monthlyBudget,
    totalMatchCount7d: total7,
    totalEstimatedMonthlyCalls: totalMonthly,
    totalEstimatedMonthlyCost: totalCost,
    overBudget: monthlyBudget != null && totalCost > monthlyBudget,
    rules: rulesProj,
  };
}

interface DatedRow extends WorklistRow {
  studyDate: string | undefined;
}

async function fetchRecentStudies(): Promise<DatedRow[]> {
  // Pull the last 30 days from the worklist. Returns up to the backend's
  // default page size — projection on huge archives would need pagination;
  // for most clinics a 30-day window is <2000 rows and fits in one page.
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  const fmt = (d: Date) => d.toISOString().slice(0, 10).replace(/-/g, '');
  try {
    const res = await apiCall<any>('wl.get', {
      studyDateFrom: fmt(from),
      studyDateTo: fmt(to),
    });
    if (res.s !== 'ok' || !Array.isArray(res.data)) return [];
    return res.data as DatedRow[];
  } catch {
    return [];
  }
}

function rowToFacts(row: WorklistRow): StudyFacts {
  return {
    studyId: String(row.studyId),
    modality: row.modality,
    bodyPart: row.bodyPart,
    studyDescription: row.studyDescription,
    institution: row.institution,
    referringPhysician: row.referringPhysician,
    patientAgeYears: parseAge(row.patientAge),
    now: new Date(),
  };
}

function parseAge(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  // EDGEPACS stores age in DICOM-style '045Y' / '018M' format.
  const m = /^(\d+)([YMWD])/i.exec(raw);
  if (!m) return undefined;
  const n = parseInt(m[1], 10);
  switch (m[2].toUpperCase()) {
    case 'Y': return n;
    case 'M': return Math.floor(n / 12);
    case 'W': return Math.floor(n / 52);
    case 'D': return Math.floor(n / 365);
    default: return n;
  }
}

function isWithinDays(studyDate: string | undefined, days: number): boolean {
  if (!studyDate || studyDate.length < 8) return false;
  try {
    const d = new Date(
      +studyDate.slice(0, 4),
      +studyDate.slice(4, 6) - 1,
      +studyDate.slice(6, 8),
    );
    const ms = Date.now() - d.getTime();
    return ms >= 0 && ms <= days * 24 * 3600 * 1000;
  } catch {
    return false;
  }
}
